import React, { useEffect, useRef } from 'react';

import { DashMap, useDashMap } from '@/design-system/dash-map';


export interface MapPoint {
  lat: number;
  lng: number;
}

/** Jakarta — where the map opens without a point yet. */
const DEFAULT_CENTER: MapPoint = { lat: -6.2088, lng: 106.8456 };
const round7 = (n: number) => Math.round(n * 1e7) / 1e7;

/**
 * The one draggable pin: click the map to drop / move it, drag it to
 * fine-tune. Lives inside <DashMap> (needs its context).
 */
const DraggablePin: React.FC<{
  point: MapPoint | null;
  onChange: (p: MapPoint) => void;
}> = ({ point, onChange }) => {
  const { map, maps } = useDashMap();
  const marker = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Map click drops / moves the pin (bound once per map).
  useEffect(() => {
    if (!map || !maps) return undefined;
    const listener = map.addListener('click', (e: any) => {
      if (!e?.latLng) return;
      onChangeRef.current({
        lat: round7(e.latLng.lat()),
        lng: round7(e.latLng.lng()),
      });
    });
    return () => listener?.remove?.();
  }, [map, maps]);

  // Marker follows the point; created on the first point, recentred after.
  useEffect(() => {
    if (!map || !maps) return;
    if (!point) {
      marker.current?.setMap(null);
      marker.current = null;
      return;
    }
    if (!marker.current) {
      marker.current = new maps.Marker({
        position: point,
        map,
        draggable: true,
        title: 'Titik tujuan — geser untuk menyesuaikan',
      });
      marker.current.addListener('dragend', (e: any) => {
        onChangeRef.current({
          lat: round7(e.latLng.lat()),
          lng: round7(e.latLng.lng()),
        });
      });
    } else {
      marker.current.setPosition(point);
    }
    map.panTo(point);
  }, [map, maps, point]);

  useEffect(
    () => () => {
      marker.current?.setMap(null);
      marker.current = null;
    },
    []
  );
  return null;
};

/**
 * The destination map in the outbound form: shows the pin for the typed /
 * resolved coordinates and lets ops click to drop it or drag it to adjust —
 * right in the form, no modal. Every move hands the coordinates back.
 */
export const DestinationMap: React.FC<{
  point: MapPoint | null;
  onChange: (p: MapPoint) => void;
}> = ({ point, onChange }) => (
  <div data-testid="oo-location-preview">
    <DashMap
      center={point ?? DEFAULT_CENTER}
      zoom={point ? 16 : 11}
      className="h-[220px] w-full rounded-lg border border-stroke-soft-200"
    >
      <DraggablePin point={point} onChange={onChange} />
    </DashMap>
  </div>
);
